import {
  Hub,
  Sensor,
  SensorEvent,
  useHubs,
  useSensors,
  useSensorEvents,
  useLogger,
  UpdateHubAddressInput,
} from 'common';
import React, { createContext, useContext, ReactNode, useEffect } from 'react';

import { useFirmwareContext } from './FirmwareContext';

interface AppSyncContextType {
  hub: Hub | null;
  hubUpdating: boolean;
  hubs: Hub[];
  hubsLoading: boolean;
  hubsError: Error | null;
  fetchHubs: () => Promise<void>;
  getHubById: (id: string) => Promise<Hub>;
  addHub: (input: any) => Promise<Hub>;
  modifyHub: (input: any) => Promise<Hub>;
  updateHubAddress: (input: UpdateHubAddressInput) => Promise<void>;
  removeHub: (id: string) => Promise<Hub>;

  sensors: Sensor[];
  sensorsLoading: boolean;
  sensorsError: Error | null;
  fetchSensorsByHub: (hubId: string) => Promise<void>;
  getSensorById: (id: string) => Promise<Sensor>;
  addSensor: (input: any) => Promise<Sensor>;
  updateSensor: (input: any) => Promise<Sensor>;
  removeSensor: (id: string) => Promise<Sensor>;

  sensorEvents: SensorEvent[];
  sensorEventsLoading: boolean;
  sensorEventsError: Error | null;
  fetchSensorEventsBySensor: (sensorId: string, limit?: number) => Promise<void>;
  fetchSensorEventsByHub: (hubId: string, limit?: number) => Promise<void>;
  addSensorEvent: (input: any) => Promise<SensorEvent>;

  selectedHubId: string | null;
  selectedSensorId: string | null;
  setSelectedHubId: (id: string | null) => void;
  setSelectedSensorId: (id: string | null) => void;

  fetchSensorHistoricalState: (
    sensorId: string,
    startTime: number,
    endTime: number,
    rollup: number,
  ) => Promise<any>;
}

const AppSyncContext = createContext<AppSyncContextType | undefined>(undefined);

export const AppSyncProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const logger = useLogger('AppSyncProvider');
  const { iotStatus } = useFirmwareContext();

  const [selectedHubId, setSelectedHubId] = React.useState<string | null>(
    iotStatus?.hub_id || null,
  );
  const [selectedSensorId, setSelectedSensorId] = React.useState<string | null>(null);

  const {
    hub,
    hubs,
    loading: hubsLoading,
    updating: hubUpdating,
    error: hubsError,
    fetchHubs,
    getHubById,
    addHub,
    modifyHub,
    updateHubAddress,
    removeHub,
  } = useHubs({ fetchAndSubscribeOnLoad: false });

  const {
    sensors,
    loading: sensorsLoading,
    error: sensorsError,
    fetchSensorsByHub,
    getSensorById,
    addSensor,
    updateSensor,
    removeSensor,
  } = useSensors(selectedHubId || undefined);

  const {
    sensorEvents,
    loading: sensorEventsLoading,
    error: sensorEventsError,
    fetchSensorEventsBySensor,
    fetchSensorEventsByHub,
    addSensorEvent,
    fetchSensorHistoricalState,
  } = useSensorEvents(selectedHubId || undefined, selectedSensorId || undefined);

  const value = {
    hub,
    hubUpdating,
    hubs,
    hubsLoading,
    hubsError,
    fetchHubs,
    getHubById,
    addHub,
    modifyHub,
    updateHubAddress,
    removeHub,

    sensors,
    sensorsLoading: sensorsLoading || false,
    sensorsError,
    fetchSensorsByHub,
    getSensorById,
    addSensor,
    updateSensor,
    removeSensor,

    sensorEvents,
    sensorEventsLoading,
    sensorEventsError,
    fetchSensorEventsBySensor,
    fetchSensorEventsByHub,
    addSensorEvent,

    selectedHubId,
    selectedSensorId,
    setSelectedHubId,
    setSelectedSensorId,

    fetchSensorHistoricalState,
  };

  useEffect(() => {
    if (iotStatus?.hub_id) {
      logger.debug(
        `retrieved hub_id from firmware context, setting selectedHubId: ${iotStatus.hub_id}`,
      );
      setSelectedHubId(iotStatus.hub_id);
      getHubById(iotStatus.hub_id);
    }
  }, [iotStatus?.hub_id]);

  return <AppSyncContext.Provider value={value}>{children}</AppSyncContext.Provider>;
};

export const useAppSync = () => {
  const context = useContext(AppSyncContext);
  if (context === undefined) {
    throw new Error('useAppSync must be used within an AppSyncProvider');
  }
  return context;
};
